import * as React from 'react';
import { useCallback, useState } from 'react';

import { Skeleton } from '@ui/components';
import { cn } from '@ui/lib/utils';

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  isSkeleton?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, placeholder, isSkeleton, ...props }, ref) => {
    if (isSkeleton) {
      return (
        <Skeleton className={cn('h-10 text-left rounded-md', className)} />
      );
    }

    const [isFocused, setIsFocused] = useState(false);

    const numberInputOnWheelPreventChange = (
      e: React.WheelEvent<HTMLInputElement>,
      type?: string,
    ) => {
      if (type !== 'number' || !isFocused) return;
      (e.target as HTMLInputElement)?.blur();

      e.stopPropagation();

      setTimeout(() => {
        (e.target as HTMLInputElement)?.focus();
      }, 0);
    };

    const handleFocus = useCallback(
      (event: React.FocusEvent<HTMLInputElement, Element>) => {
        setIsFocused(true);
        if (!props.onFocus) return;
        props.onFocus(event);
      },
      [props.onFocus],
    );

    const handleBlur = useCallback(
      (event: React.FocusEvent<HTMLInputElement, Element>) => {
        setIsFocused(false);
        if (!props.onBlur) return;
        props.onBlur(event);
      },
      [props.onBlur],
    );

    return (
      <input
        ref={ref}
        className={cn(
          'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-disabled',
          className,
        )}
        placeholder={placeholder ? placeholder : 'Type here...'}
        type={type}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onWheel={(e) => numberInputOnWheelPreventChange(e, type)}
        {...props}
      />
    );
  },
);
Input.displayName = 'Input';

export { Input };
